import PaginationControls from '@/components/pagination/PaginationControls'
import { ReactNode } from 'react'
import { MdCheck, MdClose, MdInbox } from 'react-icons/md'

export const QUEUE_PAGE_SIZE = 10

interface QueueSectionProps<T> {
    items: T[]
    loading: boolean
    /** Página atual (1-based), controlada pelo pai para sobreviver ao recarregar. */
    page: number
    onPageChange: (page: number) => void
    /** Rótulo no singular, usado na paginação e na barra de seleção. */
    label: string
    emptyTitle: string
    emptyHint?: string
    renderItem: (item: T) => ReactNode
    /** Ausente quando o usuário não pode aprovar: some a seleção em massa. */
    selection?: {
        selectedCount: number
        /**
         * Quantas o "selecionar todas" marca. Numa fila de contas a pagar são só as
         * **vencidas ou vencendo hoje** — ver `isDueForPayment` na página.
         */
        totalSelectable: number
        /**
         * Quantas ficaram de fora do "todas" por ainda não terem vencido. Exibido para
         * o número não parecer errado: a lista mostra 10 linhas e o seletor diz 3.
         */
        upcomingCount?: number
        amountText?: string
        onToggleAll: () => void
        onApprove: () => void
        onReject: () => void
        onClear: () => void
    }
}

/**
 * Casca de uma fila: seleção em massa, lista de cards e paginação.
 *
 * A paginação é **client-side**: os três endpoints de pendências devolvem a fila
 * inteira (são filas de trabalho, não históricos — devem ser esvaziadas). Paginar no
 * cliente mantém a seleção em massa coerente e evita três endpoints novos para um
 * volume que raramente passa de algumas dezenas.
 */
export default function QueueSection<T extends { id: number }>({
    items,
    loading,
    page,
    onPageChange,
    label,
    emptyTitle,
    emptyHint,
    renderItem,
    selection,
}: QueueSectionProps<T>) {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    if (items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-secondary/10 text-secondary flex items-center justify-center mb-3">
                    <MdInbox className="text-3xl" />
                </div>
                <p className="font-medium text-base-content">{emptyTitle}</p>
                {emptyHint && (
                    <p className="text-sm text-base-content/60 mt-1 max-w-md">
                        {emptyHint}
                    </p>
                )}
            </div>
        )
    }

    // A página pode ficar além do fim depois de aprovar o último item dela.
    const totalPages = Math.max(1, Math.ceil(items.length / QUEUE_PAGE_SIZE))
    const safePage = Math.min(page, totalPages)
    const start = (safePage - 1) * QUEUE_PAGE_SIZE
    const pageItems = items.slice(start, start + QUEUE_PAGE_SIZE)

    return (
        <div className="flex flex-col gap-3">
            {/* Também aparece quando NADA é selecionável mas há contas a vencer:
                sumir a barra faria o operador achar que a seleção em massa quebrou,
                quando na verdade não há o que marcar em lote hoje. */}
            {selection &&
                (selection.totalSelectable > 0 ||
                    !!selection.upcomingCount) && (
                <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-base-300/70 bg-base-200/40 px-4 py-2.5">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-sm checkbox-secondary"
                            checked={
                                selection.totalSelectable > 0 &&
                                selection.selectedCount === selection.totalSelectable
                            }
                            ref={el => {
                                if (el) {
                                    el.indeterminate =
                                        selection.selectedCount > 0 &&
                                        selection.selectedCount <
                                            selection.totalSelectable
                                }
                            }}
                            onChange={selection.onToggleAll}
                            disabled={selection.totalSelectable === 0}
                            aria-label="Selecionar vencidas"
                        />
                        {selection.selectedCount > 0 ? (
                            <span>
                                <span className="font-semibold text-secondary">
                                    {selection.selectedCount}
                                </span>{' '}
                                {selection.selectedCount === 1
                                    ? label
                                    : `${label}s`}{' '}
                                {selection.selectedCount === 1
                                    ? 'selecionada'
                                    : 'selecionadas'}
                                {selection.amountText && (
                                    <span className="text-base-content/60">
                                        {' '}
                                        · {selection.amountText}
                                    </span>
                                )}
                            </span>
                        ) : (
                            <span className="text-base-content/60">
                                {selection.upcomingCount
                                    ? `Selecionar vencidas (${selection.totalSelectable})`
                                    : `Selecionar todas (${selection.totalSelectable})`}
                                {selection.upcomingCount ? (
                                    <span
                                        className="text-xs text-warning ml-2"
                                        title="Contas com vencimento futuro não entram na seleção em massa — aprovar em lote mandaria o dinheiro adiantado. Marque uma a uma para antecipar."
                                    >
                                        {selection.upcomingCount} a vencer fora
                                        da seleção
                                    </span>
                                ) : null}
                            </span>
                        )}
                    </label>

                    {selection.selectedCount > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                className="btn btn-sm btn-success"
                                onClick={selection.onApprove}
                            >
                                <MdCheck /> Aprovar
                            </button>
                            <button
                                className="btn btn-sm btn-error btn-outline"
                                onClick={selection.onReject}
                            >
                                <MdClose /> Rejeitar
                            </button>
                            <button
                                className="btn btn-sm btn-ghost"
                                onClick={selection.onClear}
                            >
                                Limpar
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className="flex flex-col gap-2.5">
                {pageItems.map(item => (
                    <div key={item.id}>{renderItem(item)}</div>
                ))}
            </div>

            {items.length > QUEUE_PAGE_SIZE && (
                <PaginationControls
                    page={safePage}
                    limit={QUEUE_PAGE_SIZE}
                    total={items.length}
                    onPageChange={onPageChange}
                    label={label}
                />
            )}
        </div>
    )
}
